import classNames from 'classnames';
import { NextRouter, useRouter } from 'next/router';
import React, { CSSProperties, ReactNode, cloneElement } from 'react';
import { isMobile, isSayWeeeApp, isWeixinBrowser } from 'utils/common/device';

import BACK_IMAGE from '@/assets/images/latino-icon-return.png';
import Img from '@/components/common/Img';
import Constants from '@/constants/const';
import { twMerge } from 'tailwind-merge';

import useClickActionT2 from '@/tracks/useClickActionT2';
import useTheme from '@/store/theme';

type Props = {
  className?: string;
  leftNode?: ReactNode | string | null;
  rightNode?: ReactNode | string;
  children?: ReactNode | string;
  titleStyles?: CSSProperties;
};

export const Header = (props: Props) => {
  const { className, leftNode, children, rightNode, titleStyles = {} } = props;

  return (
    <div className={twMerge('w-full h-full flex relative items-center bg-surface-100-bg', className)}>
      <div className={classNames('flex h-full pl-500 items-center z-50', 'header__left')} data-role="left">
        {leftNode}
      </div>
      <div
        className="absolute left-1200 right-1200 top-0 bottom-0 flex items-center justify-center text-center"
        style={titleStyles}
        data-role="middle">
        <span className="text-ellipsis overflow-hidden whitespace-nowrap">{children}</span>
      </div>
      <div
        className={classNames('absolute right-0 flex h-full justify-end items-center pr-400 z-50', 'header__right')}
        data-role="right">
        {rightNode}
      </div>
    </div>
  );
};

type BackProps = {
  onClick?: any;
  showBackHomeIcon?: boolean;
  className?: string;
  wrapperClassName?: string;
  backIcon?: React.ReactNode;
  iconSrc?: any;
  backHomeIconClick?: (e, cb?) => void;
};

export const defaultBack = (router: NextRouter, appBackUrl?, h5BackUrl?) => {
  let referer = document?.referrer || '';
  let currentPage = window.location.pathname || '';
  appBackUrl = appBackUrl ?? '/native_app';
  h5BackUrl = h5BackUrl ?? `/${router?.locale || ''}`;
  let idx = history.state?.idx || 0;
  const isFirstLanding = sessionStorage.getItem(Constants.FIRST_PAGE_URL_IN_APP) === currentPage;
  if (isSayWeeeApp()) {
    if (sessionStorage.getItem(Constants.FIRST_PAGE_URL_IN_APP) === currentPage) {
      return (window.location.href = appBackUrl);
    }
    history.back();
    return;
  }

  if (isWeixinBrowser()) {
    if (isFirstLanding) {
      sessionStorage.removeItem(Constants.FIRST_PAGE_URL_IN_APP);
      return (window.location.href = h5BackUrl);
    }
  }

  if (referer && !referer.includes(currentPage)) {
    if (referer.includes('sayweee.') || referer.includes('masgusto.')) {
      return router.back();
    }
  }

  if (idx > 0) {
    return router.back();
  }

  if (referer) {
    if ((referer.includes('sayweee.') || referer.includes('masgusto.')) && !referer.includes(currentPage)) {
      return router.back();
    }
  }

  if (!isMobile()) {
    window.location.href = h5BackUrl;
    return;
  }
  router.push(h5BackUrl);
};

export const Back = ({
  onClick = defaultBack,
  showBackHomeIcon,
  backIcon,
  iconSrc,
  className,
  wrapperClassName,
  backHomeIconClick
}: BackProps) => {
  let router = useRouter();
  const { buttonClick } = useClickActionT2();
  const handleBack = () => {
    onClick(router);
  };
  const goHome = () => {
    if (isSayWeeeApp()) {
      window.location.href = '/';
      return;
    }
    if (!isMobile()) {
      window.location.href = '/';
      return;
    }
    router.push('/');
  };
  const handleBackHome = e => {
    buttonClick({
      mod_nm: null,
      mod_pos: null,
      sec_nm: null,
      sec_pos: null,
      co: {
        target_nm: 'weee_logo',
        target_pos: 0,
        target_type: 'normal_button',
        click_type: 'view'
      }
    });
    if (backHomeIconClick) {
      backHomeIconClick(e, goHome);
    } else {
      goHome();
    }
  };

  return (
    <>
      {backIcon ? (
        <div data-testid="btn-back" onClick={handleBack}>
          {backIcon}
        </div>
      ) : (
        <div
          data-testid="btn-back"
          className={twMerge('h-full inline-flex items-center', wrapperClassName)}
          onClick={handleBack}>
          <Img
            alt="weee page back button"
            aria-label="weee page back button"
            className={twMerge(classNames('w-600 h-600 cursor-pointer object-contain'), className)}
            src={iconSrc || BACK_IMAGE}
          />
        </div>
      )}
    </>
  );
};

type SubPageProps = {
  title?: string | React.ReactNode;
  shareData?: ObjectType;
  shareIcon?: string; // 自定义分享的icon，支持WeeeIcon type字段
  shareAfterClose?: () => void; // 分享关闭后的回调函数
  trackSharePopupClick?: (e) => void; // 分享click埋点函数
  pageUrl?: string;
  onBack?: any;
  //是否默认微信分享信息
  isServerSide?: boolean;
  shareRender?: any;
  onBeforeShareShow?: (modal: any) => void;
  showBackIcon?: boolean;
  /**左上角返回按钮右侧是否显示返回首页的按钮 */
  showBackHomeIcon?: boolean;
  backHomeIconClick?: (e, cb?) => void;
  backIcon?: React.ReactNode;
} & Props &
  ObjectType;

export const SubPageHeader = (props: SubPageProps) => {
  const {
    title,
    shareData,
    className,
    onBack,
    isServerSide,
    shareIcon,
    shareAfterClose,
    onBeforeShareShow,
    trackSharePopupClick,
    showBackHomeIcon = false,
    showBackIcon = true,
    backHomeIconClick,
    backIcon,
    ...rest
  } = props;

  const { theme } = useTheme() || {};

  return (
    <Header
      className={classNames('data-[role=left]:pl-2', className)}
      leftNode={
        showBackIcon ? (
          <Back
            onClick={onBack}
            showBackHomeIcon={showBackHomeIcon}
            backIcon={backIcon}
            backHomeIconClick={backHomeIconClick}
          />
        ) : null
      }
      {...rest}>
      {!!title && (
        <div
          data-testid={`wid-header-title`}
          className={twMerge(
            'text-primary-1 enki-body-base-medium overflow-hidden text-ellipsis whitespace-nowrap py-1',
            theme?.header?.font_color
          )}>
          {title}
        </div>
      )}
    </Header>
  );
};

export default SubPageHeader;
